import React, { useCallback, useEffect, useState } from 'react';
import { getHighLevelDomain } from '@springbig/shared';
import {
    api,
    AuthPayload,
    getStashboardData,
    rememberMeApi,
    useAppSettings,
    useAuthPin,
    useI18n,
    useRequestPin,
    useSpringBigStore,
    useCheckAuth
} from '@springbig/shared';
import { PinFormData } from './components/PinForm';
import { Caption } from '~/shared/Typography';
import { AppRouteProps, useAppNavigate } from '..';
import { useLocation } from 'react-router';
import { BirthdayStep } from '../tenDLC/components/BirthdayStep';
import { WelcomeStep } from '../tenDLC/components/WelcomeStep';
import { CreateStep } from '../tenDLC/components/CreateStep';
import LoginTemplate from './LoginTemplate';
import FooterTemplate from './FooterTemplate';
import { AuthTemplate } from './AuthTemplate';
import { Button, Modal } from '~/shared/Theme';
import { PasskeyPrompt } from '../tenDLC/components/PasskeyPrompt';
import { useToast } from '~/hooks/toast';
import { VStack } from "native-base";

const SharedPinScreen = () => {

    const [stashboardOrMemberKey, setStashboardOrMemberKey] =
        useState<AuthPayload['reference']>();

    const {
        walletId,
        setWalletId,
        setBirthdays,
        setNextStep,
        stashboardTarget,
        setBirthdayInput,
        merchantName,
        setMerchantName,
        setTendlcLogoSrc,
        stashboards,
        setStashboards
    } = useSpringBigStore();

    const { mutate: checkAuth } = useCheckAuth();
    const { mutate: authPin } = useAuthPin();
    const { mutate: requestPin, error: requestPinError } = useRequestPin();

    const { navigate, params } = useAppNavigate();
    const location = useLocation();
    const [pinError, setPinError] = useState('');
    const { defaultScreen } = useAppSettings();
    const [codeLength, setCodeLength] = useState(4);
    const [showBirthdayModal, setShowBirthdayModal] = useState(false);
    const { t } = useI18n();
    const [body, setBody] = useState<'login' | 'welcome' | 'create'>('login');
    const [phoneNumber, setPhoneNumber] = useState<string>('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [hasTriedPasskeyLogin, setHasTriedPasskeyLogin] = useState(false);
    const [showPasskeyPrompt, setShowPasskeyPrompt] = useState(false);
    const [hasPasskey, setHasPasskey] = useState(undefined);
    const [passkeyEnabled, setPasskeyEnabled] = useState(undefined);
    const { successToast } = useToast();

    const [disabled, setDisabled] = useState(false);
    const base64urlToBase64 = (base64url) => {

        let base64 = base64url.replace(/-/g, "+").replace(/_/g, "/");
        while (base64.length % 4) {
            base64 += "=";
        }

        return base64;
    };

    const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
        const binary = String.fromCharCode(...new Uint8Array(buffer));
        return btoa(binary);
    };
    
    const navigateToStashboardOrWalletPasskey = () => {

        handleSubmit(undefined, true);
    };

    const tryPasskeyLogin = async (walletId, passkeyEnabled) => {

        if (hasTriedPasskeyLogin || passkeyEnabled === false) {
            return;
        }

        setHasTriedPasskeyLogin(true);
        try {
            const options = await api.post(
                `/wallet/auth/v1/passkeys/authentication_options`,
                { 
                    wallet_id: walletId,
                    origin: getHighLevelDomain(window.location.origin)
                },
            );

            options.challenge = Uint8Array.from(
                atob(base64urlToBase64(options.challenge)),
                c => c.charCodeAt(0)
            );

            if (options.allowCredentials) {
                options.allowCredentials = options.allowCredentials.map((cred: any) => ({
                    ...cred,
                    id: Uint8Array.from(
                        atob(base64urlToBase64(cred.id)),
                        c => c.charCodeAt(0)
                    )
                }));
            }

            const credential = await navigator.credentials.get({
                publicKey: options,
            }) as PublicKeyCredential;

            if (!credential) {
                throw new Error("Credential creation failed or was canceled.");
            }

            const assertionResponse = credential.response as AuthenticatorAssertionResponse;
            const credentialPayload = {
                id: credential.id,
                rawId: arrayBufferToBase64(credential.rawId),
                type: credential.type,
                response: {
                    authenticatorData: bufferToBase64url(assertionResponse.authenticatorData),
                    clientDataJSON: bufferToBase64url(assertionResponse.clientDataJSON),
                    signature: bufferToBase64url(assertionResponse.signature),
                    userHandle: assertionResponse.userHandle
                        ? bufferToBase64url(assertionResponse.userHandle)
                        : null,
                },
                wallet_id: walletId,
                challenge_base64: options.challenge_base64,
                origin: getHighLevelDomain(window.location.origin),
            };


            const result = await api.post(
                '/wallet/auth/v1/passkeys/authenticate',
                credentialPayload
            );


            if (result === undefined) {
                throw new Error("Passkey login failed");
            }

            console.log("Passkey login success", result);
            navigateToStashboardOrWalletPasskey();
        } catch (err) {
            console.error("Passkey login failed or unavailable", err);
        } finally {
            setHasTriedPasskeyLogin(false);
        }
    };

    const stringToUint8Array = (str: string): Uint8Array => {
        return new Uint8Array([...str].map(c => c.charCodeAt(0)));
    };

    const bufferToBase64url = (buffer: ArrayBuffer): string => {
        return btoa(String.fromCharCode(...new Uint8Array(buffer)))
            .replace(/\+/g, "-")
            .replace(/\//g, "_")
            .replace(/=+$/, "");
    };

    const registerWithPasskey = async (walletId: string) => {
        try {
            const options = await api.post(
                '/wallet/auth/v1/passkeys/registration_options',
                { 
                    wallet_id: walletId,
                    origin: getHighLevelDomain(window.location.origin)
                }
            );

            if (!options?.challenge || !options?.user?.id || !options.challenge_base64) {
                throw new Error("Missing WebAuthn registration options");
            }

            options.challenge = Uint8Array.from(
                atob(base64urlToBase64(options.challenge)),
                c => c.charCodeAt(0)
            );

            options.user.id = stringToUint8Array(options.user.id);

            if (options.excludeCredentials) {
                options.excludeCredentials = options.excludeCredentials.map((cred: any) => ({
                    ...cred,
                    id: Uint8Array.from(
                        atob(base64urlToBase64(cred.id)),
                        c => c.charCodeAt(0)
                    )
                }));
            }

            options.user = {
                ...options.user,
                name: options?.user?.name || options?.user?.email || `${merchantName} User`,
                displayName: options?.user?.displayName || options?.user?.name || `${merchantName} Passkey Account`,
            };


            const credential = await navigator.credentials.create({
                publicKey: options,
            }) as PublicKeyCredential;

            if (!credential) {
                throw new Error("Credential creation failed or was canceled.");
            }

            const attestationResponse = credential.response as AuthenticatorAttestationResponse;

            const credentialPayload = {
                id: credential.id,
                rawId: bufferToBase64url(credential.rawId),
                type: credential.type,
                authenticatorAttachment: credential.authenticatorAttachment,
                response: {
                    attestationObject: bufferToBase64url(attestationResponse.attestationObject),
                    clientDataJSON: bufferToBase64url(attestationResponse.clientDataJSON),
                },
                wallet_id: walletId,
                challenge_base64: options.challenge_base64,
                origin: getHighLevelDomain(window.location.origin)
            };

            const result = await api.post(
                '/wallet/auth/v1/passkeys/register',
                credentialPayload
            );

            setHasPasskey(true);
            console.log("Passkey login success", result);
            successToast('Passkey Saved');
            navigateToStashboardOrWalletPasskey();
        } catch (err) {
            console.error("Passkey registration failed:", err);
        }
    };

    const [isResendCode, setIsResendCode] = useState(false);

    const handleResendCode = () => {

        const _params =
            stashboardOrMemberKey === 'stashboard'
                ? params.stashboard_key
                : params.wallet_id;

        if (_params) {
            createRequestPin(_params);
        }

        setDisabled(true);
        setIsResendCode(true);
    };

    const getStashboardOrMemberKey = () => {

        if (location?.state?.isStashboard && walletId) {
            navigate(`w/${walletId}`, { state: { from: location.pathname, isUnAuthorised: true, isStashboard: false } });
        }
        else if (location?.state?.isStashboard && params && params.wallet_id) {
            getAuthMember(params.wallet_id, 'stashboard');
            return 'stashboard';
        }
        else if (params && params.wallet_id) {
            getAuthMember(params.wallet_id, 'member');
            return 'member';
        }
        else if (params && params.stashboard_key) {
            getAuthMember(params.stashboard_key, 'stashboard');
            return 'stashboard';
        }
    };

    const handleSubmit = useCallback(
        (values?: PinFormData, passkey_successful?: boolean) => {

            let stashboardOrMemberKeyValue = stashboardOrMemberKey;

            if (stashboardOrMemberKeyValue === undefined) {
                stashboardOrMemberKeyValue = getStashboardOrMemberKey();
            }

            setPinError('');
            if (stashboardOrMemberKeyValue) {
                const _params =
                    stashboardOrMemberKeyValue === 'stashboard'
                        ? params.stashboard_key
                        : params.wallet_id;

                authPin({
                    stashboardOrMemberKey: _params!,
                    pin: values?.pin ? btoa(values?.pin) : '',
                    source: 'shared_pin',
                    reference: stashboardOrMemberKeyValue!,
                    merger_wallet_id: new URLSearchParams(location.search).get('merger_wallet_id'),
                    passkey_successful: passkey_successful ? passkey_successful : false
                }, {
                    onSuccess: (response) => {

                        if (response.data) {
                            setBirthdays(response.data);
                            setBirthdayInput(response.ten_dlc_birthday_input);
                        }

                        setMerchantName(response?.merchant_dba_name);
                        setTendlcLogoSrc(response?.tendlc_logo_src);
                        setNextStep(response.step);
                        setHasPasskey(response?.has_passkey);

                        if (response.step === 'birthday' && response.data) {
                            setShowBirthdayModal(true);
                            const hasPasskey = response?.data?.has_passkey;
                            setHasPasskey(hasPasskey);
                            if (hasPasskey === true) {
                                setPasskeyEnabled(response?.data?.passkey_enabled);
                                tryPasskeyLogin(response?.data?.wallet_id ?? walletId, response?.data?.passkey_enabled);
                            }
                        }
                        else if (response.step === 'create') {
                            setShowCreateModal(true);
                        }
                        else {
                            setPasskeyEnabled(response?.data?.passkey_enabled);
                            onNavigate(response.ten_dlc_flow, response?.has_passkey, response?.data?.passkey_enabled);
                        }
                    },
                    onError: (error: any) => {

                        if (error.status === 401 || error.status === 422) {
                            setPinError(t('sharedPin.errors.invalidCode'));
                        }
                    }
                });

            }
        }, [stashboardOrMemberKey, params, authPin]
    );

    const callCheckAuth = (
            id,
            reference
        ) => {
        checkAuth({
            stashboardOrMemberKey: id,
            reference,
            skip_tendlc: new URLSearchParams(location.search).get('skip10dlc') ? new URLSearchParams(location.search).get('skip10dlc') === 'true' : undefined,
            merger_wallet_id: new URLSearchParams(location.search).get('merger_wallet_id'),
        }, {
            onSuccess: (response) => {

                if (response.data) {
                    setBirthdays(response.data);
                    setBirthdayInput(response.ten_dlc_birthday_input);
                }

                setMerchantName(response?.merchant_dba_name);
                setTendlcLogoSrc(response?.tendlc_logo_src);

                setNextStep(response.step);
                if (response.step === 'birthday' && response.data) {
                    setShowBirthdayModal(true);
                    const hasPasskey = response?.data?.has_passkey;
                    setHasPasskey(hasPasskey);
                    if (hasPasskey === true) {
                        setPasskeyEnabled(response?.data?.passkey_enabled);
                        tryPasskeyLogin(response?.data?.wallet_id ?? walletId, response?.data?.passkey_enabled);
                    }
                }
                else if (response.step === 'create') {
                    setShowCreateModal(true);
                }
                else {
                    setPasskeyEnabled(response?.data?.passkey_enabled);
                    onNavigate(response.ten_dlc_flow, false, response?.data?.passkey_enabled);
                }
            },
            onError: (error: any) => {

                if (error.status === 401) {
                    setCodeLength(parseInt(error.data.pin_size));
                    if (error.data.step === 'login' && error.data.pin_size === 6) {
                        setBody('create');
                    }

                    if (error.data.step === 'birthday' && error.data.data) {
                        setBirthdays(error.data.data);
                        setBirthdayInput(error.data.ten_dlc_birthday_input);
                        setShowBirthdayModal(true);
                        const hasPasskey = error?.data?.has_passkey;
                        setHasPasskey(hasPasskey);
                        if (hasPasskey === true) {
                            setPasskeyEnabled(error?.data?.passkey_enabled);
                            tryPasskeyLogin(error?.data?.wallet_id ?? walletId, error?.data?.passkey_enabled);
                        }
                    }

                    setWalletId(error.data.merchant_member_key);
                    setMerchantName(error.data.merchant_dba_name);
                    setTendlcLogoSrc(error.data?.tendlc_logo_src);
                    setPinError('');

                    const hasPasskey = error.data?.has_passkey;
                    setHasPasskey(hasPasskey);
                    if (hasPasskey === true) {
                        setPasskeyEnabled(error?.data?.passkey_enabled);
                        tryPasskeyLogin(error?.data?.wallet_id ?? walletId, error?.data?.passkey_enabled);
                    }
                    
                }
            }
        });
    };

    const getAuthMember = useCallback(
        (id: string, reference: AuthPayload['reference']) => {
            callCheckAuth(id, reference); 
        },
        []
    );

    const onNavigate = (isTenDlc: boolean, apiHasPasskey: boolean, passkey_enabled: boolean): void => {

        const messageTarget = stashboardTarget ? `messages/${stashboardTarget}` : defaultScreen;

        setWalletId(params.wallet_id);
        let target = isTenDlc ? `w/${params.wallet_id}/tendlc?merchantName=${merchantName}` : `w/${params.wallet_id}/${(messageTarget || 'messages')}`;

        const plan_id = localStorage.getItem('plan_id');
        const link_id = localStorage.getItem('link_id');

        if (plan_id) {
            localStorage.removeItem('plan_id');
            target = `w/${params.wallet_id}/subscription/${plan_id}`;
        }
        else if (link_id) {
            target = `w/${params.wallet_id}/subscriptions`;
        }

        if (hasPasskey === false && apiHasPasskey === false && passkey_enabled) {
            handleWelcome();
            setShowPasskeyPrompt(true);
        } else {
            navigate(target as AppRouteProps);
        }
    };

    const createRequestPin = useCallback(
        (id: string) => {

            id && requestPin({
                stashboardOrMemberKey: id,
                reference: stashboardOrMemberKey!,
                pin_length: codeLength,
            }, {
                onSuccess(data) {

                    if (data) {
                        setPhoneNumber(data.phone);
                    }
                },
            });
        },
        [stashboardOrMemberKey, requestPin, codeLength]
    );

    useEffect(() => {

        document.title = 'Secure SMS Viewer | Log in';
        (document as any).description = 'Access your messages';
    }, []);

    useEffect(() => {

        setStashboardOrMemberKey(getStashboardOrMemberKey());
    }, [getAuthMember, params.stashboard_key, params.wallet_id, rememberMeApi]);

    useEffect(
        () => {

            if (requestPinError) {
                navigate('404');
            }
        },
        [requestPinError, navigate]
    );

    const handleWelcome = () => {

        if (stashboardTarget) {
            getStashboardData({
                stashboard_key: stashboardTarget.toString(),
                type: 'sms',
            }).then((data) => {

                const updatedStashboards = stashboards.map((s) => {

                    if (s._key === data._key) {
                        return { ...s, ...data };
                    }

                    return s;
                });

                if (!updatedStashboards.find((s) => s._key === data._key)) {
                    updatedStashboards.push(data);
                }

                setStashboards(updatedStashboards);
            });
        }

        setShowBirthdayModal(false);
        setShowCreateModal(false);
        setBody('welcome');
    };

    const handleChildEvent = (message: string, passkeyEnabledParam?: boolean): void => {

        if (params && params.wallet_id) {
            setWalletId(params.wallet_id);
        }

        switch (message) {
        case 'messages':
            const fetchedStashboard = stashboards.find((s) => s._key === stashboardTarget.toString());

            if (fetchedStashboard?.redirect_url) {
                window.location.replace(fetchedStashboard.redirect_url);
                return;
            }

            let target = stashboardTarget ? `w/${params.wallet_id}/messages/${stashboardTarget}` : `w/${params.wallet_id}/messages`;
            const plan_id = localStorage.getItem('plan_id');
            if (plan_id) {
                localStorage.removeItem('plan_id');
                target = `w/${params.wallet_id}/subscription/${plan_id}`;
            }

            const link_id = localStorage.getItem('link_id');
            if (link_id) {
                localStorage.removeItem('link_id');
                target = `w/${params.wallet_id}/subscriptions`;
            }

            navigate(target as AppRouteProps);
            break;
        case 'welcome':
            handleWelcome();
            if (hasPasskey === false && (passkeyEnabled || passkeyEnabledParam)) {
                setShowPasskeyPrompt(true);
            }
            
            break;
        }
    };

    const loginBody = (
        <LoginTemplate
            codeLength={codeLength || 4}
            handleSubmit={handleSubmit}
            pinError={pinError}
            disabled={disabled}
            phoneNumber={phoneNumber}
            handleResendCode={handleResendCode}
            isResendCode={isResendCode}
        />
    );

    const welcomeBody = (
        <>
            <WelcomeStep t={t} onEvent={handleChildEvent} />
        </>
    );

    const createBody = (
        <VStack justifyContent={'flex-start'} flex={1} space={4}>
            <VStack>
                <Caption
                    fontSize="24"
                    fontWeight="bold"
                    fontFamily={'Helvetica'}
                >
                    {t('sharedPin.create.title')}
                </Caption>
                <Caption fontFamily={'Helvetica'}>
                    {t('sharedPin.create.description')}
                </Caption>
                <Button
                    isDisabled={disabled}
                    text='sharedPin.create.button'
                    fontFamily={'Helvetica'}
                    onPress={() => {

                        handleResendCode();
                        setBody('login');
                    }}
                />
            </VStack>
            <FooterTemplate />
        </VStack>
    );

    const getBody = () => {

        if (body === 'login') {
            return loginBody;
        }

        if (body === 'welcome') {
            return welcomeBody;
        }

        if (body === 'create') {
            return createBody;
        }

        return null;
    };

    return (
        <AuthTemplate
            body={
                <VStack flex={1}>

                    { getBody() }
                    { showBirthdayModal &&
                        <Modal isOpen={showBirthdayModal}>
                            <BirthdayStep
                                onEvent={handleChildEvent}
                                navigate={navigate}
                                params={params}
                                setPasskeyEnabled={setPasskeyEnabled}
                            />
                        </Modal>
                    }
                    {showCreateModal && (
                        <Modal isOpen={showCreateModal}>
                            <CreateStep
                                t={t}
                                onEvent={handleChildEvent}
                                navigate={navigate}
                                params={params}
                            />
                        </Modal>
                    )}

                    {showPasskeyPrompt === true &&
                        <PasskeyPrompt
                            isOpen={showPasskeyPrompt} 
                            onClose={() => setShowPasskeyPrompt(false)}
                            onCreatePasskey={() => {
                                registerWithPasskey(walletId);
                            }}
                            onRemindLater={() => setShowPasskeyPrompt(false)}
                        />
                    }
                </VStack>
            }
        />
    );
};

export default SharedPinScreen;